import React, { useMemo, useState } from 'react';
import './ComplianceFindingsTable.css';

const STATUS_LABELS = {
  potential_violation: { text: 'Violation?', className: 'bg-danger' },
  near_miss: { text: 'Near miss', className: 'bg-warning text-dark' },
  needs_timestamp: { text: 'No timestamp', className: 'bg-info text-dark' },
};

const DISPOSITION_LABELS = {
  pending: {
    text: 'Pending review',
    icon: 'bi-hourglass-split',
    tone: 'pending',
    className: 'bg-secondary',
  },
  violation: {
    text: 'Confirmed violation',
    icon: 'bi-exclamation-triangle-fill',
    tone: 'violation',
    className: 'bg-danger',
  },
  cleared: {
    text: 'Ignore',
    icon: 'bi-eye-slash-fill',
    tone: 'cleared',
    className: 'bg-success',
  },
};

const formatQuantity = (value) => Number(value || 0).toLocaleString('en-US', {
  maximumFractionDigits: 4,
});

const formatClock = (utcValue) => {
  if (!utcValue) return '—';
  const parsed = new Date(utcValue);
  if (Number.isNaN(parsed.getTime())) return utcValue;
  return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const localTimeZoneLabel = () => (
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'
);

const formatAdv = (finding) => {
  if (finding.adv_percent === null || finding.adv_percent === undefined) {
    return <span className="text-muted">Not available</span>;
  }
  const percent = Number(finding.adv_percent);
  const display = percent < 0.01 ? '<0.01%' : `${percent.toFixed(2)}%`;
  return (
    <span className={finding.inconsequential ? 'text-success' : 'text-danger fw-semibold'}>
      {display}
    </span>
  );
};

const dayOffsetLabel = (offset) => {
  if (offset === 0) return 'Same day';
  const magnitude = Math.abs(offset);
  const unit = magnitude === 1 ? 'day' : 'days';
  return offset > 0 ? `${magnitude} ${unit} before` : `${magnitude} ${unit} after`;
};

/**
 * Read-only table of employee trades that overlapped a trade placed for
 * clients, with the reviewer controls that decide what lands in the signed
 * potential violations report.
 */
export default function ComplianceFindingsTable({ findings, onDispositionChange, busyFindingId }) {
  const [includeNearMisses, setIncludeNearMisses] = useState(true);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [securityFilter, setSecurityFilter] = useState('');
  const [reviewFilter, setReviewFilter] = useState('');

  const employees = useMemo(
    () => [...new Set(findings.map((finding) => finding.employee_name))].sort(),
    [findings],
  );
  const securities = useMemo(
    () => [...new Set(findings.map((finding) => finding.underlying))].sort(),
    [findings],
  );
  const filteredFindings = useMemo(
    () => findings.filter((finding) => (
      (includeNearMisses || finding.status !== 'near_miss')
      && (!employeeFilter || finding.employee_name === employeeFilter)
      && (!securityFilter || finding.underlying === securityFilter)
      && (!reviewFilter || finding.disposition === reviewFilter)
    )),
    [employeeFilter, findings, includeNearMisses, reviewFilter, securityFilter],
  );

  if (!findings.length) {
    return (
      <div className="alert alert-success mb-0">
        No employee trades overlapped a client trade for this period.
      </div>
    );
  }

  return (
    <div className="compliance-findings-table-wrapper">
      <div className="row g-2 align-items-end mb-3">
        <div className="col-sm-3">
          <label className="form-label small mb-1" htmlFor="compliance-employee-filter">
            Employee
          </label>
          <select
            id="compliance-employee-filter"
            className="form-select form-select-sm"
            value={employeeFilter}
            onChange={(event) => setEmployeeFilter(event.target.value)}
          >
            <option value="">All employees</option>
            {employees.map((employee) => (
              <option key={employee} value={employee}>{employee}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-3">
          <label className="form-label small mb-1" htmlFor="compliance-security-filter">
            Security
          </label>
          <select
            id="compliance-security-filter"
            className="form-select form-select-sm"
            value={securityFilter}
            onChange={(event) => setSecurityFilter(event.target.value)}
          >
            <option value="">All securities</option>
            {securities.map((security) => (
              <option key={security} value={security}>{security}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-3">
          <label className="form-label small mb-1" htmlFor="compliance-review-filter">
            Review status
          </label>
          <select
            id="compliance-review-filter"
            className="form-select form-select-sm"
            value={reviewFilter}
            onChange={(event) => setReviewFilter(event.target.value)}
          >
            <option value="">All review statuses</option>
            {Object.entries(DISPOSITION_LABELS).map(([value, option]) => (
              <option key={value} value={value}>{option.text}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-3">
          <div className="form-check mb-1">
            <input
              id="compliance-near-miss-filter"
              className="form-check-input"
              type="checkbox"
              checked={includeNearMisses}
              onChange={(event) => setIncludeNearMisses(event.target.checked)}
            />
            <label className="form-check-label small" htmlFor="compliance-near-miss-filter">
              Include near misses
            </label>
          </div>
        </div>
      </div>
      <div className="small text-muted mb-2">
        Showing {filteredFindings.length} of {findings.length} findings.
      </div>
      {!filteredFindings.length && (
        <div className="alert alert-warning mb-2">
          No findings match the selected filters.
        </div>
      )}
      <table className="table table-sm table-hover align-middle compliance-findings-table">
        <colgroup>
          <col className="compliance-column-finding" />
          <col className="compliance-column-employee" />
          <col className="compliance-column-security" />
          <col className="compliance-column-employee-trade" />
          <col className="compliance-column-client-trades" />
          <col className="compliance-column-adv" />
          <col className="compliance-column-review" />
        </colgroup>
        <thead>
          <tr>
            <th>Finding</th>
            <th>Employee</th>
            <th>Security</th>
            <th>Employee trade</th>
            <th>Client trades</th>
            <th>% of ADV</th>
            <th>Review</th>
          </tr>
        </thead>
        <tbody>
          {filteredFindings.map((finding) => {
            const statusLabel = STATUS_LABELS[finding.status] || STATUS_LABELS.near_miss;
            const dispositionLabel = DISPOSITION_LABELS[finding.disposition]
              || DISPOSITION_LABELS.pending;

            return (
              <tr key={finding.finding_id}>
                <td>
                  <span className={`badge compliance-status-badge ${statusLabel.className}`}>
                    {statusLabel.text}
                  </span>
                  <div className="small text-muted">{dayOffsetLabel(finding.day_offset)}</div>
                </td>
                <td>
                  <div>{finding.employee_name}</div>
                  <div className="small text-muted">{finding.account_label}</div>
                </td>
                <td>
                  <div className="fw-semibold">{finding.underlying}</div>
                  {finding.symbol !== finding.underlying && (
                    <div className="small text-muted">{finding.symbol}</div>
                  )}
                </td>
                <td>
                  <div>
                    {finding.employee_action} {formatQuantity(finding.employee_quantity)}
                  </div>
                  <div className="small text-muted">
                    {finding.employee_trade_date} at{' '}
                    {formatClock(finding.employee_trade_datetime_utc)}
                  </div>
                </td>
                <td>
                  <div>
                    {(finding.client_actions || []).join(' / ')}{' '}
                    {formatQuantity(finding.client_quantity)}
                  </div>
                  <div className="small text-muted">
                    {finding.client_trade_date} from{' '}
                    {formatClock(finding.client_first_datetime_utc)}
                    {' to '}
                    {formatClock(finding.client_last_datetime_utc)}
                    {' · '}
                    {finding.client_trade_count} trade
                    {finding.client_trade_count === 1 ? '' : 's'}
                  </div>
                </td>
                <td>{formatAdv(finding)}</td>
                <td className="compliance-review-cell">
                  <fieldset
                    className="compliance-review-options border-0 p-0"
                    disabled={busyFindingId === finding.finding_id}
                  >
                    <legend className="visually-hidden">Review disposition</legend>
                    {Object.entries(DISPOSITION_LABELS).map(([value, option]) => (
                      <div className="compliance-review-option" key={value}>
                        <input
                          id={`finding-${finding.finding_id}-${value}`}
                          className="compliance-review-input"
                          type="radio"
                          name={`finding-${finding.finding_id}-disposition`}
                          value={value}
                          checked={finding.disposition === value}
                          aria-label={option.text}
                          onChange={() => onDispositionChange(finding, value)}
                        />
                        <label
                          className={`compliance-review-button compliance-review-button-${option.tone}`}
                          htmlFor={`finding-${finding.finding_id}-${value}`}
                          title={option.text}
                        >
                          <i className={`bi ${option.icon}`} aria-hidden="true" />
                        </label>
                      </div>
                    ))}
                    <span className={`badge ${dispositionLabel.className}`}>
                      {dispositionLabel.text}
                    </span>
                  </fieldset>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
